import { useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';

import { updateLocalStorage as saveToLocalStorage } from '../customReports/localStorage';

const AG_GRID_AUTOCOLUMN_ID = 'ag-Grid-AutoColumn';

const useGridParamsAndLocalStorageOptions = ({
  isActive,
  pathname,
  keyname,
  disableLocalStorage,
}) => {
  const updateLocalStorage = useCallback(({ keyname, state }) => {
    if (!disableLocalStorage) {
      saveToLocalStorage({ keyname, state });
    }
  }, [disableLocalStorage]);

  const onSortChanged = useCallback(
    params => {
      updateLocalStorage({
        keyname,
        state: {
          columnState: JSON.stringify(params.api.getColumnState()),
        },
      });
    },
    [keyname, updateLocalStorage]
  );

  const onFilterChanged = useCallback(
    params => {
      updateLocalStorage({
        keyname,
        state: {
          filterModel: JSON.stringify(params.api.getFilterModel()),
        },
      });
    },
    [keyname, updateLocalStorage]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onGridColumnStateChanged = useCallback(
    debounce(params => {
      updateLocalStorage({
        keyname,
        state: {
          columnState: JSON.stringify(params.api.getColumnState()),
          columnGroupState: JSON.stringify(
            params.api.getColumnGroupState()
          ),
        },
      });
    }, 1000),
    [pathname, keyname, updateLocalStorage]
  );

  const onColumnPivotModeChanged = useCallback(
    params => {
      updateLocalStorage({
        keyname,
        state: {
          isPivotMode: params.api.isPivotMode(),
        },
      });
    },
    [keyname, updateLocalStorage]
  );

  const onFirstDataRendered = useCallback(
    ({ api }) => {
      if (disableLocalStorage) {
        return;
      }
      const reportSettings = JSON.parse(window.localStorage.getItem(keyname));
      if (!reportSettings) {
        return;
      }
      if (reportSettings.filterModel) {
        api.setFilterModel(JSON.parse(reportSettings.filterModel));
      }
      if (reportSettings.isPivotMode) {
        api.setGridOption('pivotMode', JSON.parse(reportSettings.isPivotMode));
      }
      if (reportSettings.columnState) {
        api.applyColumnState({
          state: JSON.parse(reportSettings.columnState),
          applyOrder: true,
        });
        api.moveColumns([AG_GRID_AUTOCOLUMN_ID], 0);
      }
      if (reportSettings.columnGroupState) {
        api.setColumnGroupState(
          JSON.parse(reportSettings.columnGroupState)
        );
      }
    },
    [keyname, disableLocalStorage]
  );

  return useMemo(
    () =>
      isActive
        ? {
          onFirstDataRendered,
          onSortChanged,
          onFilterChanged,
          onColumnVisible: onGridColumnStateChanged,
          onColumnPinned: onGridColumnStateChanged,
          onColumnResized: onGridColumnStateChanged,
          onColumnMoved: onGridColumnStateChanged,
          onColumnRowGroupChanged: onGridColumnStateChanged,
          onColumnValueChanged: onGridColumnStateChanged,
          onColumnPivotChanged: onGridColumnStateChanged,
          onColumnPivotModeChanged,
        }
        : {},
    [
      isActive,
      onFirstDataRendered,
      onSortChanged,
      onFilterChanged,
      onGridColumnStateChanged,
      onColumnPivotModeChanged,
    ]
  );
};

export default useGridParamsAndLocalStorageOptions;
